/**
 * NMS Variant Picker Component
 * Extends NMSBaseClass following NMS Component Instance Architecture
 * Provides variant value display updates and size chart modal functionality
 */


class NMSVariantPicker extends window.NMSBaseClass {
  #isInitialized = false;
  #eventListeners = [];

  constructor(element) {
    super();
    this.element = element; // REQUIRED: Store element reference
    this.pickerId = element.dataset.pickerId || `variant-picker-${Date.now()}`; // REQUIRED: Unique identifier

    // Store component data from Liquid
    this.sectionId = element.dataset.sectionId;
    this.productId = element.dataset.productId;
    this.formId = element.dataset.formId;

    // REQUIRED: Store in global registry for external access
    window.neemans.variantPicker = window.neemans.variantPicker || {};
    window.neemans.variantPicker[this.pickerId] = this;
  }

  init() {
    if (this.#isInitialized) return;

    super.init();
    this.#setupVariantPickerRefs();
    this.#initializeVariantState();
    this.#bindVariantEvents();
    this.#bindModalEvents();
    this.#isInitialized = true;
  }

  // REQUIRED: Scope all setup to this.element
  #setupVariantPickerRefs() {
    // Query only within component scope
    this.refs.variantInputs = this.element.querySelectorAll('input[data-option-position]');
    this.refs.valueDisplays = this.element.querySelectorAll('.nms-variant__value');
    this.refs.sizeChartButtons = this.element.querySelectorAll('.nms-size-chart-button');
    this.refs.sizeChartModals = this.element.querySelectorAll('.nms-size-chart-modal');
    this.refs.inventoryContainer = document.querySelector('.nms-inventory-container');
    this.refs.inventoryText = document.querySelector('.nms-inventory__text');

    // Load variant inventory data
    this.#loadVariantInventoryData();
  }

  #loadVariantInventoryData() {
    // Load variant inventory data from JSON script tag
    const dataScript = document.getElementById(`variant-inventory-data-${this.formId}`);
    if (dataScript) {
      try {
        this.variantInventoryData = JSON.parse(dataScript.textContent);
      } catch (error) {
        console.error('Failed to parse variant inventory data:', error);
        this.variantInventoryData = {};
      }
    } else {
      this.variantInventoryData = {};
    }

    // Load product variants for matching
    this.productVariants = window.productVariants?.[this.productId] || [];
  }

  #updateSwatchStates(fieldset, activeInput) {
    // Update visual states for swatches within a specific fieldset
    if (!fieldset || !activeInput) return;

    // Remove is-selected class from all swatches in this option group
    const allSwatches = fieldset.querySelectorAll('.block-swatch');
    allSwatches.forEach(swatch => {
      swatch.classList.remove('is-selected');
    });

    // Add is-selected class to the newly selected swatch
    const activeLabel = document.querySelector(`label[for="${activeInput.id}"]`);
    if (activeLabel && activeLabel.classList.contains('block-swatch')) {
      activeLabel.classList.add('is-selected');
    }
  }

  #initializeVariantState() {
    // Set up global data in window.neemans namespace
    this.setGlobalData(`variantPicker.${this.pickerId}`, {
      currentVariant: null,
      isLoading: false,
      modalOpen: false,
    });

    // Update inventory display for initial variant
    this.#updateInventoryDisplay();
  }

  #bindVariantEvents() {
    // Bind variant option change events
    for (const input of this.refs.variantInputs) {
      const handler = (event) => this.handleVariantChange(event);
      input.addEventListener('change', handler);
      this.#eventListeners.push({ element: input, event: 'change', handler });
    }

    // Listen to various Shopify variant change events
    const formElement = document.getElementById(this.formId);
    if (formElement) {
      const variantChangeHandler = (event) => {
        // Delay to ensure variant ID input is updated
        setTimeout(() => this.#updateInventoryDisplay(), 150);
      };

      // Listen to multiple possible events
      const events = ['variant:change', 'variant-change', 'change'];
      for (const eventName of events) {
        formElement.addEventListener(eventName, variantChangeHandler);
        this.#eventListeners.push({ element: formElement, event: eventName, handler: variantChangeHandler });
      }

      // Also listen on the product-form element
      const productForm = formElement.closest('product-form');
      if (productForm && productForm !== formElement) {
        for (const eventName of events) {
          productForm.addEventListener(eventName, variantChangeHandler);
          this.#eventListeners.push({ element: productForm, event: eventName, handler: variantChangeHandler });
        }
      }
    }
  }

  #bindModalEvents() {
    // Bind size chart button events
    for (const button of this.refs.sizeChartButtons) {
      const modalId = button.dataset.modalId;
      if (modalId) {
        const handler = (event) => {
          event.preventDefault();
          this.openSizeChartModal(modalId);
        };
        button.addEventListener('click', handler);
        this.#eventListeners.push({ element: button, event: 'click', handler });
      }
    }

    // Bind modal close events
    for (const modal of this.refs.sizeChartModals) {
      // Close button events
      const closeButtons = modal.querySelectorAll('[data-modal-id]');
      for (const closeButton of closeButtons) {
        const modalId = closeButton.dataset.modalId;
        if (modalId && closeButton.classList.contains('nms-size-chart-modal__close')) {
          const handler = (event) => {
            event.preventDefault();
            this.closeSizeChartModal(modalId);
          };
          closeButton.addEventListener('click', handler);
          this.#eventListeners.push({ element: closeButton, event: 'click', handler });
        }
      }

      // Backdrop click events
      const backdrops = modal.querySelectorAll('.nms-size-chart-modal__backdrop');
      for (const backdrop of backdrops) {
        const modalId = backdrop.dataset.modalId;
        if (modalId) {
          const handler = (event) => {
            if (event.target === backdrop) {
              this.closeSizeChartModal(modalId);
            }
          };
          backdrop.addEventListener('click', handler);
          this.#eventListeners.push({ element: backdrop, event: 'click', handler });
        }
      }
    }

    // ESC key handler for modals
    const escHandler = (event) => {
      if (event.key === 'Escape') {
        const openModals = document.querySelectorAll('.nms-size-chart-modal[style*="display: flex"]');
        for (const modal of openModals) {
          this.closeSizeChartModal(modal.id);
        }
      }
    };
    document.addEventListener('keydown', escHandler);
    this.#eventListeners.push({ element: document, event: 'keydown', handler: escHandler });
  }

  // Public API method for variant changes
  handleVariantChange(event) {
    const input = event.target;

    if (!input.checked) return;

    try {
      // Extract display value first
      const displayValue = this.#extractDisplayValue(input);

      // Find the corresponding value display span
      const fieldset = input.closest('.variant-picker__option');
      const valueSpan = fieldset?.querySelector('.nms-variant__value');

      if (valueSpan && displayValue) {
        valueSpan.textContent = displayValue;
      }

      // Update swatch visual states for this option group
      this.#updateSwatchStates(fieldset, input);

      // Update inventory display based on current variant selection
      this.#updateInventoryDisplay();


      // Dispatch variant change event
      this.dispatchNMSEvent('variant-picker:value-changed', {
        pickerId: this.pickerId,
        optionPosition: input.dataset.optionPosition,
        optionValue: displayValue || input.value,
        originalValue: input.value,
        variantId: currentVariantId,
        component: this,
      });
    } catch (error) {
      this.handleError(error, 'Failed to update variant display');
    }
  }







  #updateInventoryDisplay() {
    if (!this.refs.inventoryContainer || !this.refs.inventoryText) return;

    try {
      // Find the current variant based on selected option values
      const currentVariantId = this.#getCurrentVariantId();

      if (!currentVariantId) {
        this.refs.inventoryContainer.style.display = 'none';
        return;
      }

      const inventoryData = this.variantInventoryData[currentVariantId];

      if (inventoryData && inventoryData.inventory_management && inventoryData.available) {
        if (inventoryData.inventory_quantity <= 20) {
          // Show low stock warning
          this.refs.inventoryText.textContent = `Only ${inventoryData.inventory_quantity} left in stock`;
          this.refs.inventoryContainer.style.display = 'block';
        } else {
          // Hide inventory warning for high stock
          this.refs.inventoryContainer.style.display = 'none';
        }
      } else {
        // Hide for out of stock or no inventory management
        this.refs.inventoryContainer.style.display = 'none';
      }
    } catch (error) {
      console.error('Error updating inventory display:', error);
      this.refs.inventoryContainer.style.display = 'none';
    }
  }

  #getCurrentVariantId() {
    // Simply use the hidden variant ID input - it's the most reliable
    const formElement = document.getElementById(this.formId);
    if (!formElement) return null;

    const variantIdInput = formElement.querySelector('input[name="id"]');
    const currentVariantId = variantIdInput?.value;


    return currentVariantId;
  }

  #getSelectedOptionValues() {
    const formElement = document.getElementById(this.formId);
    if (!formElement) return [];

    const selectedValues = [];
    const optionInputs = formElement.querySelectorAll('input[data-option-position]:checked');

    for (let i = 1; i <= 3; i++) {
      const input = Array.from(optionInputs).find(inp => inp.dataset.optionPosition === i.toString());
      if (input) {
        // Get the display value for this option, not the ID
        const displayValue = this.#extractDisplayValue(input);
        selectedValues[i - 1] = displayValue || input.getAttribute('title') || input.getAttribute('aria-label');
      } else {
        selectedValues[i - 1] = null;
      }
    }

    return selectedValues;
  }

  #findVariantByOptions(selectedOptions) {
    // Match against the actual product variants data
    for (const variant of this.productVariants) {
      let matches = true;

      for (let i = 0; i < selectedOptions.length; i++) {
        const selected = selectedOptions[i];
        const variantOption = variant.options[i];

        if (selected !== null && variantOption !== null) {
          // Try to match the options (handling size prefixes)
          if (selected !== variantOption &&
              selected !== variantOption.replace(/^(UK|US|EU)\s+/, '') &&
              variantOption !== selected.replace(/^(UK|US|EU)\s+/, '')) {
            matches = false;
            break;
          }
        } else if (selected !== variantOption) {
          matches = false;
          break;
        }
      }

      if (matches) {
        return variant;
      }
    }

    return null;
  }

  #getSelectedOptionIds() {
    // Helper method to get all currently selected option IDs
    const formElement = document.getElementById(this.formId);
    if (!formElement) return [];

    const selectedIds = [];
    const optionInputs = formElement.querySelectorAll('input[data-option-position]:checked');

    for (let i = 1; i <= 3; i++) {
      const input = Array.from(optionInputs).find(inp => inp.dataset.optionPosition === i.toString());
      selectedIds[i - 1] = input ? input.value : null;
    }

    return selectedIds;
  }

  #getOptionValueText(input) {
    // Fallback method to get option value text
    const label = input.closest('label') || input.nextElementSibling;
    if (label && label.tagName === 'LABEL') {
      return label.textContent.trim().replace(/\s+/g, ' ');
    }
    return input.value;
  }

  #extractDisplayValue(input) {
    let displayValue = '';

    if (input.type === 'radio') {
      // Look for the label associated with this input
      let label = input.nextElementSibling;
      if (!label || label.tagName !== 'LABEL') {
        // For inputs inside labels, find the parent label
        label = input.closest('label');
      }

      if (label) {
        // For dropdown style options, look for spans with option text
        const optionSpan = label.querySelector('span:first-child');
        if (optionSpan) {
          const textContent = optionSpan.textContent.trim();
          if (textContent && !textContent.includes('Sold out') && !textContent.includes('Unavailable')) {
            displayValue = textContent;
          }
        }

        // If no span found, get the label's text content directly
        if (!displayValue) {
          const labelText = label.textContent.trim();
          // Filter out common status text
          if (labelText && !labelText.includes('Sold out') && !labelText.includes('Unavailable')) {
            displayValue = labelText;
          }
        }
      }

      // For anchors (in dropdowns), check for span text
      if (!displayValue) {
        const anchor = input.nextElementSibling;
        if (anchor && anchor.tagName === 'A') {
          const spanInAnchor = anchor.querySelector('span:first-child');
          if (spanInAnchor) {
            const anchorText = spanInAnchor.textContent.trim();
            if (anchorText && !anchorText.includes('Sold out') && !anchorText.includes('Unavailable')) {
              displayValue = anchorText;
            }
          }
        }
      }
    }

    // Clean up the display value
    return displayValue ? displayValue.replace(/\s+/g, ' ').trim() : '';
  }

  // Public API methods for size chart modals
  openSizeChartModal(modalId) {
    const modal = document.getElementById(modalId);
    if (modal) {
      modal.style.display = 'flex';
      document.body.style.overflow = 'hidden';

      // Update global state
      this.setGlobalData(`variantPicker.${this.pickerId}.modalOpen`, true);

      // Dispatch modal open event
      this.dispatchNMSEvent('variant-picker:modal-opened', {
        pickerId: this.pickerId,
        modalId: modalId,
        component: this,
      });
    }
  }

  closeSizeChartModal(modalId) {
    const modal = document.getElementById(modalId);
    if (modal) {
      modal.style.display = 'none';
      document.body.style.overflow = '';

      // Update global state
      this.setGlobalData(`variantPicker.${this.pickerId}.modalOpen`, false);

      // Dispatch modal close event
      this.dispatchNMSEvent('variant-picker:modal-closed', {
        pickerId: this.pickerId,
        modalId: modalId,
        component: this,
      });
    }
  }

  // Public API methods for external access
  getCurrentVariant() {
    return this.getGlobalData(`variantPicker.${this.pickerId}.currentVariant`);
  }



  isModalOpen() {
    return this.getGlobalData(`variantPicker.${this.pickerId}.modalOpen`) || false;
  }

  destroy() {
    // Clean up event listeners
    for (const listener of this.#eventListeners) {
      listener.element.removeEventListener(listener.event, listener.handler);
    }
    this.#eventListeners = [];

    // Clean up global reference
    if (window.neemans.variantPicker?.[this.pickerId]) {
      delete window.neemans.variantPicker[this.pickerId];
    }

    super.destroy();
    this.#isInitialized = false;
  }
}

// REQUIRED: Standard initialization pattern
function initNMSVariantPicker() {
  if (!window.NMSBaseClass) {
    console.error('NMSVariantPicker: NMSBaseClass required');
    return;
  }


  // Find all variant picker components
  const elements = document.querySelectorAll('[data-nms-variant-picker]');

  for (const element of elements) {
    if (!element.hasAttribute('data-initialized')) {
      const instance = new NMSVariantPicker(element);
      instance.init();
      element.setAttribute('data-initialized', 'true');
    }
  }
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNMSVariantPicker);
} else {
  initNMSVariantPicker();
}

// Handle theme editor reloads
document.addEventListener('shopify:section:load', event => {
  if (event.target.querySelector('[data-nms-variant-picker]')) {
    initNMSVariantPicker();
  }
});

// Re-initialize on NMS content updates (from color swatches, etc.)
document.addEventListener('nms:color-content-updated', event => {
  if (event.detail.container?.querySelector('[data-nms-variant-picker]')) {
    initNMSVariantPicker();
  }
});

// Make available in global namespace
window.neemans = window.neemans || {};
window.neemans.NMSVariantPicker = NMSVariantPicker;

// Global initialization function for other components to use
window.initNMSVariantPicker = initNMSVariantPicker;

// Legacy global functions for backwards compatibility (if needed)
window.openSizeChartModal = function(modalId) {
  // Find the appropriate component instance and call the method
  const instances = Object.values(window.neemans.variantPicker || {});
  for (const instance of instances) {
    if (instance.element.querySelector(`#${modalId}`)) {
      instance.openSizeChartModal(modalId);
      break;
    }
  }
};

window.closeSizeChartModal = function(modalId) {
  // Find the appropriate component instance and call the method
  const instances = Object.values(window.neemans.variantPicker || {});
  for (const instance of instances) {
    if (instance.element.querySelector(`#${modalId}`)) {
      instance.closeSizeChartModal(modalId);
      break;
    }
  }
};
